/**
 * Marketplace Health Report Page
 * Displays comprehensive overview of platform's operational performance
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  MenuItem,
  IconButton,
  Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner } from '../../components/common';
import { MarketplaceHealthService } from '../../services/reports/MarketplaceHealthService';
import { formatDate } from '../../utils/dateUtils';
import type { MarketplaceHealthData, KPICard, ReportFilters } from '../../types/reports';
import {
  Card,
  CardContent,
  Select,
} from '../../components/ui';

const MarketplaceHealthPage: React.FC = () => {
  const theme = useTheme();
  const [data, setData] = useState<MarketplaceHealthData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<ReportFilters>({
    dateRange: 'Last 30 Days',
    region: 'All Regions',
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await MarketplaceHealthService.getMarketplaceHealthData(filters);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch marketplace health data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getTrendIcon = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ fontSize: 14 }} />;
      case 'down':
        return <TrendingDownIcon sx={{ fontSize: 14 }} />;
      default:
        return <RemoveIcon sx={{ fontSize: 14 }} />;
    }
  };

  const getTrendColor = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return theme.palette.success.main;
      case 'down':
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const renderKPICard = (kpi: KPICard) => (
    <Card
      sx={{
        border: `1px solid ${theme.customColors.tableBorder}`,
        height: '100%',
      }}
    >
      <CardContent>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            marginBottom: theme.spacing(1),
            fontWeight: 500,
          }}
        >
          {kpi.title}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
          }}
        >
          {kpi.value}
        </Typography>
        {kpi.change && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Box sx={{ color: getTrendColor(kpi.trend) }}>
              {getTrendIcon(kpi.trend)}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: getTrendColor(kpi.trend),
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              {kpi.change}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (isLoading && !data) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No data available.
        </Typography>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(0.5),
          }}
        >
          Marketplace Health Report
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(3),
          }}
        >
          Comprehensive overview of the platform's operational performance.
        </Typography>

        {/* Filters Section */}
        <Card
          sx={{
            border: `1px solid ${theme.customColors.tableBorder}`,
            marginBottom: theme.spacing(3),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: theme.spacing(2),
              cursor: 'pointer',
            }}
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: '1rem',
              }}
            >
              Filters
            </Typography>
            <IconButton size="small">
              {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={filtersOpen}>
            <Box
              sx={{
                padding: theme.spacing(2),
                paddingTop: 0,
                display: 'flex',
                gap: theme.spacing(2),
                flexWrap: 'wrap',
              }}
            >
              <Select
                size="small"
                label="Date Range"
                value={filters.dateRange || 'Last 30 Days'}
                onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as string })}
                formControlProps={{ sx: { minWidth: 150 } }}
              >
                <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
                <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
                <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
                <MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
                <MenuItem value="Custom">Custom</MenuItem>
              </Select>

              <Select
                size="small"
                label="Region"
                value={filters.region || 'All Regions'}
                onChange={(e) => setFilters({ ...filters, region: e.target.value as string })}
                formControlProps={{ sx: { minWidth: 150 } }}
              >
                <MenuItem value="All Regions">All Regions</MenuItem>
                <MenuItem value="Michigan">Michigan</MenuItem>
                <MenuItem value="National">National</MenuItem>
              </Select>

              <Select
                size="small"
                label="Asset Type"
                value="All Types"
                formControlProps={{ sx: { minWidth: 150 } }}
              >
                <MenuItem value="All Types">All Types</MenuItem>
                <MenuItem value="Corporate">Corporate</MenuItem>
                <MenuItem value="Individual">Individual</MenuItem>
              </Select>

              <Select
                size="small"
                label="Ghost Tier"
                value="All Tiers"
                formControlProps={{ sx: { minWidth: 150 } }}
              >
                <MenuItem value="All Tiers">All Tiers</MenuItem>
                <MenuItem value="Tier 1">Tier 1</MenuItem>
                <MenuItem value="Tier 2">Tier 2</MenuItem>
                <MenuItem value="Tier 3">Tier 3</MenuItem>
              </Select>
            </Box>
          </Collapse>
        </Card>
      </Box>

      {/* Supply and Demand Section */}
      <Box sx={{ marginBottom: theme.spacing(4) }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(2),
            fontSize: '1rem',
          }}
        >
          Supply and Demand
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            {renderKPICard(data.supplyAndDemand.activeGhosts)}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            {renderKPICard(data.supplyAndDemand.newGhosts)}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            {renderKPICard(data.supplyAndDemand.churnRate)}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            {renderKPICard(data.supplyAndDemand.activeAssets)}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            {renderKPICard(data.supplyAndDemand.completedTasks)}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            {renderKPICard(data.supplyAndDemand.failedTasks)}
          </Grid>
        </Grid>
      </Box>

      {/* Charts Section - Supply vs Demand and MAU */}
      <Grid container spacing={3} sx={{ marginBottom: theme.spacing(4) }}>
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: '1rem',
                }}
              >
                Supply vs. Demand Trend
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={data.supplyVsDemandTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                  <XAxis
                    dataKey="date"
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                    tickFormatter={(value) => formatDate(value)}
                  />
                  <YAxis
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="supply"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Supply"
                  />
                  <Line
                    type="monotone"
                    dataKey="demand"
                    stroke={theme.palette.secondary.main}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Demand"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: '1rem',
                }}
              >
                Monthly Active Users (MAU) Comparison
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data.mauComparison}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                  <XAxis
                    dataKey="category"
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                  />
                  <YAxis
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                  />
                  <Legend />
                  <Bar dataKey="ghostMAU" fill={theme.palette.primary.main} name="Ghost MAU" />
                  <Bar dataKey="assetMAU" fill={theme.palette.secondary.main} name="Asset MAU" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Matching Efficiency Section */}
      <Box sx={{ marginBottom: theme.spacing(4) }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(2),
            fontSize: '1rem',
          }}
        >
          Matching Efficiency
        </Typography>
        <Grid container spacing={3} sx={{ marginBottom: theme.spacing(3) }}>
          <Grid item xs={12} sm={6} md={4}>
            {renderKPICard(data.matchingEfficiency.matchSuccessRate)}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {renderKPICard(data.matchingEfficiency.avgMatchingTime)}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {renderKPICard(data.matchingEfficiency.autoAssignmentRate)}
          </Grid>
        </Grid>

        {/* Matching Efficiency Charts */}
        {data.matchSuccessTrend && data.autoVsManualAssignment && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  border: `1px solid ${theme.customColors.tableBorder}`,
                  minHeight: 300,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      marginBottom: theme.spacing(2),
                      fontSize: '1rem',
                    }}
                  >
                    Match Success Rate Trend
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={data.matchSuccessTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                      <XAxis
                        dataKey="date"
                        stroke={theme.palette.text.secondary}
                        style={{ fontSize: '0.75rem' }}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return `${date.getMonth() + 1}/${date.getDate()}`;
                        }}
                      />
                      <YAxis
                        stroke={theme.palette.text.secondary}
                        style={{ fontSize: '0.75rem' }}
                        domain={[95, 100]}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.customColors.tableBorder}`,
                          borderRadius: theme.shape.borderRadius,
                        }}
                        labelStyle={{ color: theme.palette.text.primary }}
                        formatter={(value: number) => `${value.toFixed(2)}%`}
                      />
                      <Line
                        type="monotone"
                        dataKey="rate"
                        stroke={theme.palette.success.main}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        name="Success Rate"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  border: `1px solid ${theme.customColors.tableBorder}`,
                  minHeight: 300,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      marginBottom: theme.spacing(2),
                      fontSize: '1rem',
                    }}
                  >
                    Auto vs. Manual Assignment
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.autoVsManualAssignment}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                      <XAxis
                        dataKey="date"
                        stroke={theme.palette.text.secondary}
                        style={{ fontSize: '0.75rem' }}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return `${date.getMonth() + 1}/${date.getDate()}`;
                        }}
                      />
                      <YAxis
                        stroke={theme.palette.text.secondary}
                        style={{ fontSize: '0.75rem' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.customColors.tableBorder}`,
                          borderRadius: theme.shape.borderRadius,
                        }}
                        labelStyle={{ color: theme.palette.text.primary }}
                      />
                      <Legend />
                      <Bar dataKey="auto" stackId="a" fill={theme.palette.primary.main} name="Auto" />
                      <Bar dataKey="manual" stackId="a" fill={theme.palette.secondary.main} name="Manual" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Fulfillment Quality Section */}
      <Box sx={{ marginBottom: theme.spacing(4) }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(2),
            fontSize: '1rem',
          }}
        >
          Fulfillment Quality
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            {renderKPICard(data.fulfillmentQuality.onTimeArrivalRate)}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {renderKPICard(data.fulfillmentQuality.piAcceptanceRate)}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {renderKPICard(data.fulfillmentQuality.ghostUtilizationRate)}
          </Grid>
        </Grid>
      </Box>

      {/* Quality Metrics Trend */}
      <Card
        sx={{
          border: `1px solid ${theme.customColors.tableBorder}`,
        }}
      >
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              marginBottom: theme.spacing(2),
              fontSize: '1rem',
            }}
          >
            Quality Metrics Trend
          </Typography>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={data.qualityMetricsTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
              <XAxis
                dataKey="date"
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '0.75rem' }}
                tickFormatter={(value) => formatDate(value)}
              />
              <YAxis
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '0.75rem' }}
                domain={[0, 100]}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.customColors.tableBorder}`,
                  borderRadius: theme.shape.borderRadius,
                }}
                labelStyle={{ color: theme.palette.text.primary }}
                formatter={(value: number) => `${value.toFixed(1)}%`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="onTimeArrival"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={{ r: 3 }}
                name="On-Time Arrival Rate"
              />
              <Line
                type="monotone"
                dataKey="piAcceptance"
                stroke={theme.palette.info.main}
                strokeWidth={2}
                dot={{ r: 3 }}
                name="PI Acceptance Rate"
              />
              <Line
                type="monotone"
                dataKey="ghostUtilization"
                stroke={theme.palette.success.main}
                strokeWidth={2}
                dot={{ r: 3 }}
                name="Ghost Utilization Rate"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default MarketplaceHealthPage;

